import axios from "axios";
import { AuthResponse } from "../types/auth.type";
import { Reit } from "../types/reit.types";
import { Invit } from "../types/invit.types";

type ReitLocationItem = {
  location: string;
  properties: string[];
};

const register = (name: string, mail: string, password: string) => {
  return axios.post<AuthResponse>(
    `${import.meta.env.VITE_API_URL}/auth/register`,
    {
      name,
      mail,
      password,
    }
  );
};

const login = async (email: string, password: string) => {
  const response = await axios.post(
    `${import.meta.env.VITE_API_URL}/auth/login`,
    {
      name: email,
      password,
    }
  );
  return response.data;
};

const logout = async () => {
  await axios.get(`${import.meta.env.VITE_API_URL}/auth/logout`);
};

const refreshToken = async (): Promise<string | null> => {
  const storedUser = localStorage.getItem("User");
  if (storedUser) {
    try {
      const user: AuthResponse = JSON.parse(storedUser);

      if (user.refresh_token) {
        const response = await axios.post<AuthResponse>(
          `${import.meta.env.VITE_API_URL}/auth/refresh-token`,
          {
            refreshToken: user.refresh_token,
          }
        );

        if (response.data.access_token) {
          user.access_token = response.data.access_token;
          localStorage.setItem("User", JSON.stringify(user));
          localStorage.setItem("access_token", response.data.access_token);
        }

        return response.data.access_token;
      }
    } catch (err) {
      console.error("Failed to parse user from localStorage:", err);
    }
  }
  return null;
};

const requestPasswordReset = async (
  email: string
): Promise<{ message: string }> => {
  try {
    const response = await axios.post<{ message: string }>(
      `${import.meta.env.VITE_API_URL}/auth/forgot-password`,
      { email }
    );
    return response.data;
  } catch (error: any) {
    console.error("Failed to send password reset request:", error);
    throw error;
  }
};

const resetPassword = async (
  token: string,
  password: string
): Promise<{ message: string }> => {
  try {
    const response = await axios.post<{ message: string }>(
      `${import.meta.env.VITE_API_URL}/auth/reset-password`,
      {
        token,
        password,
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 10000,
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Failed to reset password:", error);
    throw error;
  }
};

// This function returns tokens after successful OTP verification
const verifyOtp = async (email: string, otp: string): Promise<AuthResponse> => {
  try {
    const response = await axios.post<AuthResponse>(
      `${import.meta.env.VITE_API_URL}/auth/verify-otp`,
      { email, otp }
    );
    return response.data;
  } catch (error: any) {
    console.error("Failed to verify OTP:", error);
    throw error;
  }
};

const resendOtp = async (email: string): Promise<{ message: string }> => {
  try {
    const response = await axios.post<{ message: string }>(
      `${import.meta.env.VITE_API_URL}/auth/resend-otp`,
      { email }
    );
    return response.data;
  } catch (error: any) {
    console.error("Failed to resend OTP:", error);
    throw error;
  }
};

const getReits = async () => {
  try {
    const response = await axios.get(`${import.meta.env.VITE_API_URL}/reit`);
    return response.data;
  } catch (error) {
    console.error("Failed to fetch REITs:", error);
    throw error;
  }
};

const getInvits = async (isPublic: boolean) => {
  try {
    const response = await axios.get(`${import.meta.env.VITE_API_URL}/invits`, {
      params: { isPublic },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching InvITs listing:", error);
    throw error;
  }
};

const getInvitsOperational = async (isPublic: boolean) => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/invits/compare/operational`,
      {
        params: { isPublic },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching InvITs listing:", error);
    throw error;
  }
};

const calculateValuation = async (projectName: string, wacc?: number) => {
  try {
    const params = new URLSearchParams({
      projectName: projectName,
    });

    if (wacc !== undefined) {
      params.append("wacc", wacc.toString());
    }

    const response = await axios.get(
      `${
        import.meta.env.VITE_API_URL
      }/valuations/calculate?${params.toString()}`
    );
    return response.data;
  } catch (error) {
    console.error("Error calculating valuation:", error);
    throw error;
  }
};

const getReitById = async (id: string): Promise<Reit | null> => {
  try {
    const response = await axios.get<Reit>(
      `${import.meta.env.VITE_API_URL}/reit/${id}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching REIT with id ${id}:`, error);
    return null;
  }
};

const getInvitById = async (id: string): Promise<Invit | null> => {
  try {
    const response = await axios.get<Invit>(
      `${import.meta.env.VITE_API_URL}/invits/${id}`
    );
    return response.data;
  } catch (error) {
    console.error(`Error fetching Invit with id ${id}:`, error);
    return null;
  }
};

const getAdjacentByNames = async (names: string[]): Promise<any> => {
  const user = JSON.parse(localStorage.getItem("User") || "null");

  if (user?.refresh_token) {
    try {
      const joinedNames = names.join(",");
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/adjacent/${encodeURIComponent(
          joinedNames
        )}`,
        {
          headers: {
            Authorization: `Bearer ${user.refresh_token}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching adjacent data:", error);
      throw error;
    }
  } else {
    console.warn("No refresh token found in localStorage");
    return null;
  }
};

const compareAdjacent = async (value: string) => {
  const user = JSON.parse(localStorage.getItem("User") || "null");

  if (user?.refresh_token) {
    try {
      const response = await axios.get(
        `${
          import.meta.env.VITE_API_URL
        }/adjacent/findbyname/name?name=${encodeURIComponent(value)}`,
        {
          headers: {
            Authorization: `Bearer ${user.refresh_token}`,
          },
        }
      );

      if (response.data && response.data.length > 0) {
        return response.data;
      }
    } catch (error) {
      console.error("Error in compareAdjacent:", error);
      return null;
    }
  } else {
    console.warn("No access token found in localStorage");
  }

  return null;
};

const fetchCities = async (): Promise<any[]> => {
  try {
    const response = await axios.get(
      `${import.meta.env.VITE_API_URL}/adjacent/cities`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching cities:", error);
    return [];
  }
};

const getReitLocationsAndProperties = async (
  reitId: string
): Promise<ReitLocationItem[]> => {
  const user = JSON.parse(localStorage.getItem("User") || "null");

  if (user?.refresh_token) {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/adjacent/locations?id=${encodeURIComponent(
          reitId
        )}`,
        {
          headers: {
            Authorization: `Bearer ${user.refresh_token}`,
          },
        }
      );

      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error("Error in getReitLocationsAndProperties:", error);
      return []; // ⬅ ALWAYS return array
    }
  } else {
    console.warn("No access token found in localStorage");
    return []; // ⬅ ALWAYS return array
  }
};



const compareReit = async (): Promise<any | null> => {
  const user = JSON.parse(localStorage.getItem("User") || "null");

  if (user?.refresh_token) {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/reit/compare/data`,
        {
          headers: {
            Authorization: `Bearer ${user.refresh_token}`,
          },
        }
      );

      if (response.data && Object.keys(response.data).length > 0) {
        return response.data;
      }
    } catch (error) {
      console.error("Error in compareReit:", error);
      return null;
    }
  } else {
    console.warn("No access token found in localStorage");
  }

  return null;
};

const valuationAdjacent = async (value: string): Promise<any | null> => {
  const user = JSON.parse(localStorage.getItem("User") || "null");

  if (user?.refresh_token) {
    try {
      const response = await axios.get(
        `${
          import.meta.env.VITE_API_URL
        }/adjacent/valuation?id=${encodeURIComponent(value)}`,
        {
          headers: {
            Authorization: `Bearer ${user.refresh_token}`,
          },
        }
      );

      if (response.data && Object.keys(response.data).length > 0) {
        return response.data;
      }
    } catch (error) {
      console.error("Error in valuationAdjacent:", error);
      return null;
    }
  } else {
    console.warn("No access token found in localStorage");
  }

  return null;
};

export default {
  register,
  login,
  refreshToken,
  logout,
  requestPasswordReset,
  resendOtp,
  verifyOtp,
  resetPassword,
  getReits,
  getInvits,
  getReitById,
  getInvitById,
  getAdjacentByNames,
  fetchCities,
  compareAdjacent,
  compareReit,
  valuationAdjacent,
  getInvitsOperational,
  calculateValuation,
  getReitLocationsAndProperties
};
